/* eslint-disable @typescript-eslint/no-explicit-any */
/********************************
 * Global State Definitions
 ******************************** */

import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { api } from 'lib/api';

import { useOnCompanyChangeBPAResetAtoms } from './Admin/Dashboard/BPADashboard/hooks';
import { useOnCompanyChangeCAResetAtoms } from './Admin/Dashboard/CADashboard/hooks';
import { useOnCompanyChangeEpResetAtoms } from './Admin/Dashboard/EPDashboard/hooks';
import { useOnCompanyChangePCResetAtoms } from './Admin/Dashboard/PCDashboard/hooks';
import { useOnCompanyChangeSast2ResetAtoms } from './Admin/Dashboard/SASTDashboard2/hooks';
import { ChartType, DashboardType } from './Admin/Dashboard/types';
import { useOnCompanyChangeWaResetAtoms } from './Admin/Dashboard/WADashboard/hooks';
import { useOnCompanyChangeBPAFindingsResetAtoms } from './BPAFindings/state';
import { useOnCompanyChangeActivationTabConfigResetAtoms } from './Company/Configuration/tabs/CAActivationKeysTab/state';
import { useOnCompanyChangeIgnoredQIDTabConfigResetAtoms } from './Company/Configuration/tabs/IgnoredQIDsConfigTab/state';
import { useOnCompanyChangeFindingsResetAtoms } from './Findings/state';
import { useOnCompanyChangeMembersConfigResetAtoms } from './Members/state';
import { useOnCompanyChangePCFindingsResetAtoms } from './PCFindings/state';

export const companyDataAtom = atom(async () => {
  try {
    const responseData = await api.company.getAllCompanies({
      limit: 550,
      ordering: 'display_name',
    });
    return responseData?.results;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
  }
  return [];
});

// Identity atoms (company id/name/logo + active dashboard) use getOnInit:true so
// their FIRST frame is read from localStorage instead of the default (-1 / '' /
// CA). getOnInit snapshots storage once at module load, so it covers boot and
// refresh; in-session company switches are additionally handled by seeding the
// fresh store (JotaiStoreProvider). currentChartTypeAtom deliberately does NOT
// use getOnInit (cosmetic-only default, kept per product decision).
const _baseCompanyIdAtom = atomWithStorage<number>(
  'currentCompanyId',
  -1,
  undefined,
  { getOnInit: true },
);
export const currentCompanyAtom = atomWithStorage<string>(
  'currentCompany',
  '',
  undefined,
  { getOnInit: true },
);
export const companyLogoAtom = atomWithStorage<string>(
  'companyLogo',
  '',
  undefined,
  { getOnInit: true },
);

// Intentionally sync — reads only _baseCompanyIdAtom (localStorage).
// Decoupled from companyDataAtom to avoid async cascade on company switch.
// Fallback to first company (when ID is -1) is handled by LogoDropdown.
export const companyIdAtom = atom(
  (get) => get(_baseCompanyIdAtom),
  (_get, set, newValue: number) => {
    set(_baseCompanyIdAtom, newValue);
  },
);

// export const currentDashboard = atom(Dashboard.WAS)
export const currentDashboardAtom = atomWithStorage(
  'dashboard_type',
  DashboardType.CA,
  undefined,
  { getOnInit: true },
);
export const currentChartTypeAtom = atomWithStorage<ChartType>(
  'dashboard_chart_type',
  ChartType.Bar,
);
export type HostActiveStatusFilterType = 'active' | 'aging' | 'deleted';

export const enabledModulesAtom = atom(async (get) => {
  const companyId = get(companyIdAtom);
  const companies = await get(companyDataAtom);

  if (companyId <= 0 || !companies || companies.length === 0) return [];

  const currentCompany = companies.find((company) => company.id === companyId);

  return currentCompany?.enabled_modules || [];
});

export function useOnCompanyChangeResetAtoms() {
  const resetCaAtoms = useOnCompanyChangeCAResetAtoms();
  const resetEpAtoms = useOnCompanyChangeEpResetAtoms();
  const resetWaAtoms = useOnCompanyChangeWaResetAtoms();
  const resetSast2Atoms = useOnCompanyChangeSast2ResetAtoms();

  // finding listing
  const resetFindingListingAtoms = useOnCompanyChangeFindingsResetAtoms();

  // reset PC findings atoms
  const resetPCFindingsAtoms = useOnCompanyChangePCFindingsResetAtoms();
  const resetPCDashboardAtoms = useOnCompanyChangePCResetAtoms();

  // reset BPA findings atoms
  const resetBPAFindingsAtoms = useOnCompanyChangeBPAFindingsResetAtoms();
  const resetBPADashboardAtoms = useOnCompanyChangeBPAResetAtoms();

  // members config
  const resetMembersConfigAtoms = useOnCompanyChangeMembersConfigResetAtoms();

  // Activation config
  const resetActivationConfigAtoms =
    useOnCompanyChangeActivationTabConfigResetAtoms();

  // Ignore QID config
  const resetIgnoreQIDAtoms = useOnCompanyChangeIgnoredQIDTabConfigResetAtoms();
  const resetHosts = () => {
    resetCaAtoms();
    resetEpAtoms();
    resetWaAtoms();
    resetSast2Atoms();
    resetFindingListingAtoms();
    resetPCFindingsAtoms();
    resetPCDashboardAtoms();
    resetBPAFindingsAtoms();
    resetBPADashboardAtoms();
    resetMembersConfigAtoms();
    resetActivationConfigAtoms();
    resetIgnoreQIDAtoms();
  };

  return resetHosts;
}
